import { Component, inject, OnInit, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { CommonModule, Location } from '@angular/common'
import { Store } from '@ngrx/store'
import {
	getClient,
	getOrders,
	loadingOrderHistory,
	OrderHistory,
	OrderHistoryActions,
} from '@monorepo-channels/channels/domain'
import { combineLatest, filter, map, take } from 'rxjs'
import { Router, RouterOutlet } from '@angular/router'
import { CancelOrderComponent } from '../dialog/cancel-order.component'
import { MatDialog } from '@angular/material/dialog'
import { LoadingPageComponent } from '@monorepo-channels/components/ui-porto-bank'
import { DataLayerService, isMobile, MenuOpened } from '@monorepo-channels/shared/util-helpers'
import { animate, style, transition, trigger } from '@angular/animations'

@Component({
	selector: 'feature-porto-bank-order-history',
	standalone: true,
	imports: [CommonModule, MatIconModule, MatButtonModule, RouterOutlet, LoadingPageComponent],
	templateUrl: './order-history.component.html',
	styleUrls: ['./order-history.component.scss'],
	animations: [
		trigger('slideIn', [
			transition(':enter', [
				style({ transform: 'translateX(100%)', opacity: 0 }),
				animate('300ms ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
			]),
			transition(':leave', [
				animate('100ms ease-in', style({ transform: 'translateX(-100%)', opacity: 0 })),
			]),
		]),
	],
})
export class OrderHistoryComponent implements OnInit {
	private store = inject(Store)
	private orders$ = this.store.select(getOrders)
	public menuOpened = inject(MenuOpened)
	private dataLayerService = inject(DataLayerService)
	isMobile = isMobile()
	private categorizedOrders$ = this.orders$.pipe(
		map(orders => {
			const allOrders = [...(orders ?? [])]

			const activeOrders = allOrders
				.filter(o => o.active)
				.sort((a, b) => {
					const dateA = new Date(a.reservationDateTime).getTime()
					const dateB = new Date(b.reservationDateTime).getTime()
					return dateB - dateA
				})

			const inactiveOrders = allOrders
				.filter(o => !o.active)
				.sort((a, b) => {
					const dateA = new Date(a.reservationDateTime).getTime()
					const dateB = new Date(b.reservationDateTime).getTime()
					return dateB - dateA
				})

			return { activeOrders, inactiveOrders }
		})
	)

	public router = inject(Router)
	public loadingHistory$ = this.store.select(loadingOrderHistory)
	public selectedTab = signal('Ativos')
	public dialog = inject(MatDialog)
	public location = inject(Location)

	public vm$ = combineLatest([this.categorizedOrders$, this.loadingHistory$]).pipe(
		map(([orders, loading]) => ({
			activeOrders: orders.activeOrders,
			inactiveOrders: orders.inactiveOrders,
			loading,
		}))
	)

	ngOnInit(): void {
		this.dataLayerService.logEvent({
			event: 'screen_view',
			screen_name: 'Gastronomia Porto Bank - Reservas e Check-ins',
		})
		this.store
			.select(getClient)
			.pipe(
				filter(client => !!client?.id),
				take(1)
			)
			.subscribe(() => {
				this.store.dispatch(OrderHistoryActions.loadOrders())
			})
	}

	selectTab(tab: string): void {
		this.selectedTab.set(tab)
	}

	cancelOrder(order: OrderHistory | null) {
		if (!order) return
		this.dialog.open(CancelOrderComponent, {
			data: order,
			width: '400px',
		})
	}

	selectOrder(orderId: string): void {
		this.store.dispatch(OrderHistoryActions.setSelectedOrderId({ orderId }))
		this.router.navigate(['reservas-e-checkins', 'pedido', orderId])
	}

	getIntentLabel(order: OrderHistory): string {
		if (order.originalIntent === 'reservation') {
			if (order.isCanceled) return 'Reserva Cancelada'
			if (order.wasPaid === true) return 'Reserva Utilizada'
			return 'Reserva'
		}

		if (order.originalIntent === 'checkin') {
			if (order.isCanceled) return 'Check-in Cancelado'
			if (order.wasPaid === true) return 'Check-in Utilizado'
			return 'Check-in'
		}

		return 'Order sem intent'
	}
}
